import React from 'react'
import {shouldUpdate, StateUpdaters, withStateHandlers} from 'recompose'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  extractStickerState,
  getColumnColorHex,
  getColumnColorNumber,
  Hub,
  TaskData
} from 'yougile-model'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {DivWithHint, Hint} from '@components/common/hint/hint'
import {MessageBox} from '@components/message-box'
import {UserAvatar} from '@components/user-avatar'
import {noPropagate} from '@containers/hooks'
import {
  CannotCreateLinkCycleError,
  Gantt,
  LinkCycleEncounteredError
} from '@extensions/gantt'
import {globals} from '@extensions/globals-base'
import {Task as TaskExt} from '@extensions/task'

interface TaskAssignedProps {
  assigned: DataObjectId[]
  zoom?: number
}
export const TaskAssigned = (props: TaskAssignedProps) => {
  const {assigned, zoom = 1} = props
  const bem = Util.bemFrom('timeline-task')
  const px = (baseSizeInPx: number) => Math.round(baseSizeInPx * zoom) + 'px'
  return (
    <>
      {assigned.length > 0 && (
        <div className={bem('assigned')}>
          {assigned.slice(0, 3).map(uid => (
            <UserAvatar
              key={uid}
              userId={uid}
              className={bem('ava')}
              size={Math.round(24 * zoom)}
              style={{flex: `0 0 ${px(24)}`}}
              title=""
            />
          ))}
        </div>
      )}
    </>
  )
}

interface Props {
  task: TaskData
  top: number
  width: number
  height: number
  startTime: number
  dragged?: boolean
  hide?: boolean
  hideHint?: boolean
  dependencyHover: boolean
  zoom: number
  getClientLeft: () => number
  click: () => void
  dragStart?: (e: React.DragEvent) => void
  dragEnd?: () => void
  updateDates: (newStartDate: number, newDeadline: number) => void
  mouseEnter?: () => void
  mouseLeave?: () => void
}

interface State {
  changedStartDate?: number
  changedDeadline?: number
  draggingEnd: boolean
  highlighted: number
}

interface Handlers {
  changeStartDate?: (startDate: number) => State
  changeDeadline?: (deadline: number) => State
  setDraggingEnd?: (draggingEnd: boolean) => State
  setHightlighted?: (highlighted: number) => State
}

const day = 24 * 3600_000

// eslint-disable-next-line max-lines-per-function
const TaskBase = (props: Props & State & Handlers) => {
  const {
    task,
    top,
    width,
    height,
    startTime,
    click,
    dragEnd,
    dragStart,
    hide,
    hideHint,
    dragged,
    changedStartDate,
    changeDeadline,
    changeStartDate,
    changedDeadline,
    getClientLeft,
    updateDates,
    mouseLeave = Util.noop,
    mouseEnter = Util.noop,
    draggingEnd,
    setDraggingEnd,
    highlighted,
    setHightlighted,
    dependencyHover,
    zoom
  } = props

  const bem = Util.bemFrom('timeline-task')

  const px = (baseSizeInPx: number) => Math.round(baseSizeInPx * zoom) + 'px'

  const toPos = (time: number) =>
    Math.round((width * (time - startTime)) / day) + 'px'
  const deadline = changedDeadline || extractStickerState(task.deadline)
  const startDate =
    changedStartDate ||
    extractStickerState(task.startDate) ||
    extractStickerState(task.deadline)
  const taskWidth = startDate
    ? Math.round(
        (Time.middleOfDay(deadline) - Time.middleOfDay(startDate) + day) / day
      ) * width
    : width
  const taskStartDate = startDate || deadline
  const clientToDate = (x: number) => {
    const d = Math.round((getClientLeft() + x) / width)
    return startTime + d * day
  }
  const startResize = (side: 'left' | 'right') => () => {
    logger.log('start', side)
    if (side === 'left') {
      changeStartDate(taskStartDate)
    } else {
      changeDeadline(deadline)
    }
    Hint.hide()
  }
  const move = (e: React.MouseEvent) => {
    const newDate = Time.middleOfDay(clientToDate(e.clientX))
    if (changedDeadline) {
      if (
        newDate - day !== Time.middleOfDay(deadline) &&
        Time.endOfDay(newDate - day) > Time.middleOfDay(startDate)
      ) {
        changeDeadline(newDate - day)
      }
    } else {
      if (
        newDate !== startDate &&
        Time.middleOfDay(newDate) < Time.endOfDay(deadline)
      ) {
        changeStartDate(newDate)
      }
    }
  }
  const stopResize = () => {
    if (changedDeadline) {
      if (!task.startDate) {
        updateDates(
          Time.middleOfDay(extractStickerState(task.deadline)),
          changedDeadline
        )
      } else {
        updateDates(undefined, changedDeadline)
      }
      changeDeadline(undefined)
    } else {
      updateDates(changedStartDate, undefined)
      changeStartDate(undefined)
    }
  }

  const resizing = !!changedStartDate || !!changedDeadline
  const contentWidthEstimate =
    9 * task.title.length + (task.assigned ? 30 : 0) + 24

  const svgId = 'timeline-task__arrowDragSvg'

  let x: number
  let y: number
  let pointX: number
  let pointY: number
  let path: SVGPathElement
  const moveHndl = (e: MouseEvent) => {
    if ((x === e.clientX && y === e.clientY) || !pointX || !pointY) {
      return
    }
    x = e.clientX
    y = e.clientY
    requestAnimationFrame(() => {
      if (path) {
        path.setAttributeNS(
          null,
          'd',
          `M ${pointX} ${pointY} L ${e.clientX} ${e.clientY}`
        )
      }
    })
  }

  const arrowDragStart = (isDependent: boolean) => (e: React.DragEvent) => {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'copyMove'
    DragData.set(e, {type: 'task', id: task.id, isDependent})
    requestAnimationFrame(() => setDraggingEnd(true))
    const rect = (e.target as HTMLDivElement).getBoundingClientRect()
    logger.log(e.target, rect)
    pointY = isDependent ? rect.bottom : rect.top
    pointX = rect.left + (isDependent ? 0 : rect.width)

    document.addEventListener('dragover', moveHndl)

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.id = svgId
    svg.classList.add(bem('svg'))

    path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.classList.add(bem('path'))
    svg.appendChild(path)
    document.body.appendChild(svg)
  }

  const arrowDragEnd = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggingEnd(false)
    logger.log('ARR end', task.title)
    e.stopPropagation()

    document.removeEventListener('dragover', moveHndl)
    path = undefined
    const svg = document.getElementById(svgId)
    if (svg) {
      svg.remove()
    }
  }

  const drop = (e: React.DragEvent) => {
    e.preventDefault()
    const data = DragData.get()
    if (data && data.type === 'task' && data.id !== task.id) {
      const box = Sync.getCurrentSandbox()
      const to = box.get(data.isDependent ? data.id : task.id) as Hub
      const from = box.get(data.isDependent ? task.id : data.id) as Hub

      try {
        Gantt.linkTasks(from, to)
      } catch (e) {
        if (e instanceof LinkCycleEncounteredError) {
          MessageBox.warn(Lang.msg.timeline.errorLinkCycleEncountered)
        } else if (e instanceof CannotCreateLinkCycleError) {
          MessageBox.warn(Lang.msg.timeline.errorCannotCreateLinkCycle)
        }
      }

      setHightlighted(0)
    }
  }

  const dragEnter = (e: React.DragEvent) => {
    const data = DragData.get()
    if (data && data.type === 'task' && data.id !== task.id) {
      setHightlighted(highlighted + 1)
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    logger.log('enter', task.title)
  }

  const dragOver = (e: React.DragEvent) => e.preventDefault()

  const dragLeave = (_e: React.DragEvent) => {
    const data = DragData.get()
    if (data && data.type === 'task' && data.id !== task.id) {
      setHightlighted(highlighted - 1)
    }
    logger.log('leave', task.title)
  }

  const columnColor = getColumnColorHex(
    getColumnColorNumber(
      TaskExt.getById(TaskExt.findContainingColumnId(task.id))
    ),
    globals.theme,
    true
  )

  return (
    <DivWithHint
      className={bem({
        completed: !!task.completeTime,
        dragged,
        hide,
        withBg: !!columnColor,
        dragLeft: !!changedStartDate,
        resizing,
        archived: !dragged && task.archived,
        draggingEdge: draggingEnd
      })}
      style={{
        left: toPos(Time.middleOfDay(taskStartDate)),
        width: taskWidth - 8 + 'px',
        height: height + 'px',
        top: top + 'px',
        backgroundColor: columnColor
      }}
      content={
        task.title + (task.archived ? ` (${Lang.msg.taskCompleted})` : '')
      }
      key={task.id}
      onClick={click}
      draggable={!resizing}
      onDragStart={dragStart}
      onDragEnd={dragEnd}
      hideHint={hideHint || resizing || contentWidthEstimate < taskWidth}
      onMouseOver={mouseEnter}
      onMouseOut={mouseLeave}
      onMouseDown={noPropagate}
      onDrop={drop}
      onDragEnter={dragEnter}
      onDragLeave={dragLeave}
      onDragOver={dragOver}
    >
      {resizing && (
        <div
          className={bem('overlay')}
          onMouseUp={stopResize}
          onMouseMove={move}
        />
      )}

      {(highlighted > 0 || dependencyHover) && (
        <div className={bem('highlight')} />
      )}

      {!dragged && (
        <>
          <div className={bem('start-point')}>
            <div className={bem('start-point-line')} />
            <div
              className={bem('start-point-drag')}
              draggable={!resizing}
              onDragStart={arrowDragStart(true)}
              onDragEnd={arrowDragEnd}
            />
          </div>

          <div className={bem('end-point')}>
            <div className={bem('end-point-line')} />
            <div
              className={bem('end-point-drag')}
              draggable={!resizing}
              onDragStart={arrowDragStart(false)}
              onDragEnd={arrowDragEnd}
            />
          </div>

          <div className={bem('left')} onMouseDown={startResize('left')} />
          <TaskAssigned assigned={task.assigned} zoom={zoom} />

          <div
            className={bem('text')}
            style={{
              fontSize: px(16)
            }}
          >
            {task.title}
          </div>
          {!!task.archived && (
            <div className={bem('archive-icon')}>
              <DivWithHint
                content={Lang.msg.taskCompleted}
                className={bem('archive-icon-cnt')}
              />
            </div>
          )}
          <div className={bem('right')} onMouseDown={startResize('right')} />
        </>
      )}
    </DivWithHint>
  )
}

const handlers: StateUpdaters<Props, State, Handlers> = {
  changeStartDate: state => changedStartDate => ({...state, changedStartDate}),
  changeDeadline: state => changedDeadline => ({...state, changedDeadline}),
  setDraggingEnd: state => draggingEnd => ({...state, draggingEnd}),
  setHightlighted: state => highlighted => ({...state, highlighted})
}

const Comp = shouldUpdate((prev: Props & State, next: Props & State) => {
  return (
    prev.dragged !== next.dragged ||
    prev.height !== next.height ||
    prev.changedStartDate !== next.changedStartDate ||
    prev.changedDeadline !== next.changedDeadline ||
    prev.hide !== next.hide ||
    prev.hideHint !== next.hideHint ||
    prev.startTime !== next.startTime ||
    prev.top !== next.top ||
    prev.width !== next.width ||
    prev.draggingEnd !== next.draggingEnd ||
    prev.highlighted !== next.highlighted ||
    prev.dependencyHover !== next.dependencyHover ||
    !ObjUtil.deepEqual(prev.task, next.task, false)
  )
})(TaskBase)

export const Task = withStateHandlers(
  {
    changedStartDate: undefined,
    changedDeadline: undefined,
    draggingEnd: false,
    highlighted: 0
  },
  handlers
)(Comp as any)
